import { type ReactNode, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Sidebar } from '@/components/layout/sidebar';
import { Header } from '@/components/layout/header';
import { ConfigurableFooter } from '@/components/shared/configurable-footer';
import { useFooterConfig } from '@/hooks/use-footer-config';
import { AiChatPanel } from '@/components/ai/ai-chat-panel';
import { AiFloatingButton } from '@/components/ai/ai-floating-button';
import { ProfileReviewAlert } from '@/components/shared/profile-review-alert';
import { useAiChatStore } from '@/stores/ai-chat-store';
import { useSidebarStore } from '@/stores/sidebar-store';
import { useAuthStore } from '@/stores/auth-store';
import { cn } from '@/lib/utils';

/**
 * Glass 家族 shell（基线）—— 从原 `DashboardLayout` 平移而来，**首屏零视觉变化**。
 *
 * 浮空半透左侧栏 + 顶栏 + 内容区 + footer；SYSTEM_ADMIN 附 AI 面板。
 *
 * 这是其他家族 shell 的参照基线：同一份 shell 数据（导航/用户/折叠/footer/AI），
 * 不同的空间语法。neon/ink/luxury/soft/refined 各自重写本组件的几何与导航范式。
 */
export function GlassShell({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { isCollapsed } = useSidebarStore();
  const user = useAuthStore((state) => state.user);
  const { config: footerConfig } = useFooterConfig();
  const aiIsOpen = useAiChatStore((s) => s.isOpen);
  const aiIsFloating = useAiChatStore((s) => s.isFloating);
  const isSystemAdmin = user?.activeRole === 'SYSTEM_ADMIN';

  const prefersReducedMotion = useMemo(
    () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    [],
  );

  return (
    <>
      {/* Desktop sidebar - hidden on mobile */}
      <div className="hidden md:block">
        <Sidebar />
      </div>

      <div
        className={cn(
          'transition-[margin] duration-300 flex items-start min-h-screen',
          isCollapsed ? 'md:ml-20' : 'md:ml-64',
        )}
      >
        <div className="flex-1 min-w-0">
          <Header />

          <main id="main-content" className="p-3 md:p-4 lg:p-6">
            <ProfileReviewAlert />
            <AnimatePresence>
              <motion.div
                key={location.pathname}
                initial={prefersReducedMotion ? false : { opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={prefersReducedMotion ? undefined : { opacity: 0, position: 'absolute' }}
                transition={{ duration: 0.15 }}
              >
                {children}
              </motion.div>
            </AnimatePresence>
          </main>

          <ConfigurableFooter variant="dashboard" config={footerConfig} />
        </div>
        {isSystemAdmin && aiIsOpen && !aiIsFloating && <AiChatPanel mode="fixed" />}
      </div>
      {isSystemAdmin && aiIsFloating && <AiChatPanel mode="floating" />}
      {isSystemAdmin && !aiIsOpen && <AiFloatingButton />}
    </>
  );
}
